/**
 * QuoteServicesCPQ.js
 * Componente per la gestione CPQ dei servizi (installazione e burocrazia)
 */

import { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
    Trash,
    Pencil,
    Warning,
    TrendUp,
    TrendDown,
    Wrench,
    FileText,
    Check,
    X
} from '@phosphor-icons/react';

// Formattazione valuta
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

// Formattazione percentuale
const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
};

// Badge margine
const MarginBadge = ({ margin, percent }) => {
    let bgColor = 'bg-green-100 text-green-700';
    let Icon = TrendUp;
    
    if (margin < 0) {
        bgColor = 'bg-red-100 text-red-700';
        Icon = TrendDown;
    } else if (percent < 10) {
        bgColor = 'bg-amber-100 text-amber-700';
        Icon = Warning;
    }
    
    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${bgColor}`}>
            <Icon className="w-3 h-3" />
            <span>{formatCurrency(margin)}</span>
            <span className="opacity-70">({formatPercent(percent)})</span>
        </div>
    );
};

// Riga servizio editabile
const ServiceRow = ({ line, index, isEditable, onUpdate, onDelete, canViewMargins }) => {
    const [editing, setEditing] = useState(false);
    const [editValues, setEditValues] = useState({
        quantity: line.quantity || 1,
        customer_unit_price: line.customer_unit_price || 0,
        purchase_unit_price: line.purchase_unit_price || 0,
        discount_percent: line.discount_percent || 0
    });
    
    const handleSave = async () => {
        await onUpdate(index, {
            quantity: parseFloat(editValues.quantity),
            customer_unit_price: parseFloat(editValues.customer_unit_price),
            purchase_unit_price: parseFloat(editValues.purchase_unit_price),
            discount_percent: parseFloat(editValues.discount_percent)
        });
        setEditing(false);
    };
    
    const handleCancel = () => {
        setEditValues({
            quantity: line.quantity || 1,
            customer_unit_price: line.customer_unit_price || 0,
            purchase_unit_price: line.purchase_unit_price || 0,
            discount_percent: line.discount_percent || 0
        });
        setEditing(false);
    };
    
    const warnings = line.warnings || [];
    const hasWarnings = warnings.length > 0;
    
    return (
        <TableRow className={!line.included ? 'opacity-50' : ''}>
            {/* Incluso */}
            <TableCell className="w-10">
                <Switch
                    checked={line.included !== false}
                    onCheckedChange={(checked) => onUpdate(index, { included: checked })}
                    disabled={!isEditable}
                />
            </TableCell>
            
            {/* Servizio */}
            <TableCell>
                <div className="flex items-start gap-2">
                    <div>
                        <div className="font-medium text-sm">{line.description}</div>
                        {line.notes && <div className="text-xs text-zinc-500">{line.notes}</div>}
                    </div>
                    {hasWarnings && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Warning className="w-4 h-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <ul className="text-xs">
                                        {warnings.map((w, i) => <li key={`w-${i}-${(w || '').slice(0, 24)}`}>• {w}</li>)}
                                    </ul>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </TableCell>
            
            {/* Quantità */}
            <TableCell className="w-20">
                {editing ? (
                    <Input
                        type="number"
                        className="w-16 h-8 text-sm"
                        value={editValues.quantity}
                        onChange={(e) => setEditValues({...editValues, quantity: e.target.value})}
                    />
                ) : (
                    <span>{line.quantity} {line.unit}</span>
                )}
            </TableCell>
            
            {/* Costo Interno - Solo se può vedere margini */}
            {canViewMargins && (
                <TableCell className="text-right">
                    {editing ? (
                        <Input
                            type="number"
                            step="0.01"
                            className="w-24 h-8 text-sm"
                            value={editValues.purchase_unit_price}
                            onChange={(e) => setEditValues({...editValues, purchase_unit_price: e.target.value})}
                        />
                    ) : (
                        <div className="text-zinc-500 text-sm">
                            <div>{formatCurrency(line.purchase_unit_price)}</div>
                            <div className="text-xs">{formatCurrency(line.purchase_total)}</div>
                        </div>
                    )}
                </TableCell>
            )}
            
            {/* Prezzo Cliente */}
            <TableCell className="text-right">
                {editing ? (
                    <Input
                        type="number"
                        step="0.01"
                        className="w-24 h-8 text-sm"
                        value={editValues.customer_unit_price}
                        onChange={(e) => setEditValues({...editValues, customer_unit_price: e.target.value})}
                    />
                ) : (
                    <div className="font-medium">{formatCurrency(line.customer_unit_price)}</div>
                )}
            </TableCell>
            
            {/* Sconto */}
            <TableCell className="w-20">
                {editing ? (
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step="0.1"
                            className="w-14 h-8 text-sm"
                            value={editValues.discount_percent}
                            onChange={(e) => setEditValues({...editValues, discount_percent: e.target.value})}
                        />
                        <span className="text-xs">%</span>
                    </div>
                ) : (
                    line.discount_percent > 0 ? (
                        <Badge variant="secondary" className="text-xs">
                            -{line.discount_percent}%
                        </Badge>
                    ) : null
                )}
            </TableCell>
            
            {/* Totale Cliente */}
            <TableCell className="text-right font-semibold">
                {formatCurrency(line.customer_total)}
            </TableCell>
            
            {/* Margine - Solo se può vedere */}
            {canViewMargins && (
                <TableCell>
                    <MarginBadge margin={line.margin_amount} percent={line.margin_percent} />
                </TableCell>
            )}
            
            {/* Azioni */}
            <TableCell className="w-24">
                {isEditable && (
                    <div className="flex items-center gap-1">
                        {editing ? (
                            <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
                                    <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancel}>
                                    <X className="w-4 h-4 text-red-600" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)}>
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(index)}>
                                    <Trash className="w-4 h-4" />
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

// Componente principale
export default function QuoteServicesCPQ({ 
    quote, 
    isEditable, 
    onUpdateLine, 
    onDeleteLine,
    onOpenServiceCatalog,
    userRole = 'admin'
}) {
    // Determina se l'utente può vedere i margini
    const canViewMargins = useMemo(() => {
        return ['admin', 'commerciale'].includes(userRole);
    }, [userRole]);
    
    // Filtra servizi installazione
    const installationServices = useMemo(() => {
        return (quote.lines || [])
            .map((line, index) => ({ ...line, originalIndex: index }))
            .filter(line => line.line_type === 'INSTALLATION_SERVICE');
    }, [quote.lines]);
    
    // Filtra servizi burocratici
    const bureaucraticServices = useMemo(() => {
        return (quote.lines || [])
            .map((line, index) => ({ ...line, originalIndex: index }))
            .filter(line => line.line_type === 'BUREAUCRATIC_SERVICE');
    }, [quote.lines]);
    
    const handleUpdate = async (originalIndex, data) => {
        try {
            await onUpdateLine(originalIndex, data);
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        }
    };
    
    const handleDelete = async (originalIndex) => {
        if (window.confirm('Eliminare questo servizio?')) {
            try {
                await onDeleteLine(originalIndex);
                toast.success('Servizio eliminato');
            } catch (error) {
                toast.error('Errore nell\'eliminazione');
            }
        }
    };
    
    const ServiceTable = ({ services, title, icon: Icon, total, onAdd }) => (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <div>
                        <CardTitle className="text-lg">{title}</CardTitle>
                        <CardDescription>
                            {services.length} servizi • Totale: {formatCurrency(total)}
                        </CardDescription>
                    </div>
                </div>
                {isEditable && (
                    <Button variant="outline" onClick={onAdd}>
                        Aggiungi Servizio
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {services.length === 0 ? (
                    <div className="text-center py-6 text-zinc-500">
                        <Icon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Nessun servizio aggiunto</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Servizio</TableHead>
                                <TableHead className="w-20">Qtà</TableHead>
                                {canViewMargins && <TableHead className="text-right">Costo Int.</TableHead>}
                                <TableHead className="text-right">Prezzo Cliente</TableHead>
                                <TableHead className="w-20">Sconto</TableHead>
                                <TableHead className="text-right">Totale</TableHead>
                                {canViewMargins && <TableHead>Margine</TableHead>}
                                <TableHead className="w-24">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {services.map((line) => (
                                <ServiceRow
                                    key={line.originalIndex}
                                    line={line}
                                    index={line.originalIndex}
                                    isEditable={isEditable}
                                    onUpdate={handleUpdate}
                                    onDelete={handleDelete}
                                    canViewMargins={canViewMargins}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
    
    return (
        <div className="space-y-4">
            {/* Servizi Installazione */}
            <ServiceTable
                services={installationServices}
                title="Servizi di Installazione"
                icon={Wrench}
                total={quote.total_installation}
                onAdd={() => onOpenServiceCatalog('INSTALLAZIONE')}
            />
            
            {/* Servizi Burocratici */}
            <ServiceTable
                services={bureaucraticServices}
                title="Servizi Burocratici"
                icon={FileText}
                total={quote.total_services}
                onAdd={() => onOpenServiceCatalog('BUROCRAZIA')}
            />
        </div>
    );
}
